import { useMemo } from "react";
import type { TFunction } from "i18next";
import U2000SaveCancelDialog from "../../../../../u2000Core/components/dialog/U2000SaveCancelDialog";
import U2000InputText from "../../../../../u2000Core/components/formControls/input/U2000InputText";
import type { U3148OrganizationPersonalizationHandler } from "./U3148OrganizationPersonalizationHandlers";
import U2000ComboBox from "../../../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import { ORGANIZATION_PERSONALIZATION_NAME_TYPES } from "../../../common/organizationPersonalizationTypes";
import { useTranslation } from "react-i18next";
import { U2000FormStateEnumEditMode } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import { SysOrgPersonalizationNameType, U3148_TIME_SESSION_ROUNDING_OPTIONS } from "../../../../U3148Service";

interface U3148OrganizationPersonalizationDlgProps {
  u3148OrganizationPersonalizationHandler: U3148OrganizationPersonalizationHandler;
  t: TFunction;
  NS: string;
}

function U3148OrganizationPersonalizationEditDlg({ u3148OrganizationPersonalizationHandler, t, NS }: U3148OrganizationPersonalizationDlgProps) {
  const { formStateHandlers } = u3148OrganizationPersonalizationHandler;
  const { i18n } = useTranslation();

  const handleClose = (action: "save" | "cancel") => {
    if (action === "cancel") {
      const originalOnBlur = formStateHandlers.onBlur;
      formStateHandlers.onBlur = () => {
        /* Ignore onBlur during cancel */
      };

      u3148OrganizationPersonalizationHandler.onDlgClose("cancel");

      setTimeout(() => {
        formStateHandlers.onBlur = originalOnBlur;
      }, 0);
    } else if (action === "save") {
      u3148OrganizationPersonalizationHandler.onDlgClose("save");
    }
  };

  const isEditMode = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.EDIT;
  const isDialogOpen = u3148OrganizationPersonalizationHandler.isDialogOpen;
  const selectedPersonalizationName = formStateHandlers.getData()?.personalizationName ?? "";
  const isTimeSessionRoundingType =
    selectedPersonalizationName === SysOrgPersonalizationNameType.ORG_TIME_SESSION_ROUNDING_MINUTES;

  const existingPersonalizations = u3148OrganizationPersonalizationHandler.gridStateHandlers.getData();
  const existingPersonalizationNames = new Set(existingPersonalizations.map((p) => p.personalizationName));

  const availableTypes = isEditMode
    ? ORGANIZATION_PERSONALIZATION_NAME_TYPES
    : ORGANIZATION_PERSONALIZATION_NAME_TYPES.filter((type) => !existingPersonalizationNames.has(type.value));

  const formattedData = availableTypes.map((type) => ({
    value: type.value,
    label: typeof type.label === "object" ? (i18n.language === "fr" ? type.label.fr : type.label.en) : String(type.label),
  }));

  const timeSessionRoundingOptions = useMemo(
    () =>
      U3148_TIME_SESSION_ROUNDING_OPTIONS.map((minutes) => ({
        value: String(minutes),
        label: t("lblTimeSessionRoundingMinutes", { ns: NS, count: minutes }),
      })),
    [t, NS],
  );

  const valuePlaceholder =
    selectedPersonalizationName === SysOrgPersonalizationNameType.ORG_TIMEZONE
      ? t("lblPersonalizationOrgTimezoneHint", { ns: NS })
      : undefined;

  const renderValueField = () => {
    if (isTimeSessionRoundingType) {
      return (
        <U2000ComboBox
          formStateHandlers={formStateHandlers}
          fieldName="personalizationValue"
          label={t("lblPersonalizationValue", { ns: NS })}
          data={timeSessionRoundingOptions}
          keyField="value"
          descField="label"
          required={true}
          placeholder={t("lblSelectTimeSessionRounding", { ns: NS })}
          getValue={(item) => item.value}
          maxHeight={120}
          findItemByValue={(value, data) => data.find((item) => item.value === value) || null}
        />
      );
    }

    return (
      <U2000InputText
        fieldName="personalizationValue"
        label={t("lblPersonalizationValue", { ns: NS })}
        formStateHandlers={formStateHandlers}
        required={true}
        placeholder={valuePlaceholder}
      />
    );
  };

  return (
    <U2000SaveCancelDialog
      isOpen={isDialogOpen}
      onClose={handleClose}
      title={t("tabPersonalization", { ns: NS })}
      isValid={true}
      height="h-[450px]"
    >
      <div className="flex flex-col gap-y-4">
        <U2000ComboBox
          formStateHandlers={formStateHandlers}
          fieldName="personalizationName"
          label={t("lblPersonalizationName", { ns: NS })}
          data={formattedData}
          keyField="value"
          descField="label"
          required={true}
          placeholder={t("lblSelectPersonalizationName", { ns: NS })}
          readonly={isEditMode}
          getValue={(item) => item.value}
          maxHeight={200}
          findItemByValue={(value, data) => data.find((item) => item.value === value) || null}
        />
        {renderValueField()}
      </div>
    </U2000SaveCancelDialog>
  );
}

export default U3148OrganizationPersonalizationEditDlg;
